import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal, Stagger } from "@/components/Reveal";
import { Input } from "@/components/ui/input";

const FILTERS = ["All Flavors", "Bestsellers", "New", "Seasonal"];

function SkeletonCard() {
    return (
        <div className="rounded-3xl border border-caramel/10 bg-white p-4 pt-16">
            <div className="mx-auto -mt-12 mb-6 h-32 w-32 rounded-full skeleton" />
            <div className="mx-auto h-4 w-24 rounded skeleton" />
            <div className="mx-auto mt-3 h-6 w-40 rounded skeleton" />
        </div>
    );
}

export default function Shop() {
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("All Flavors");
    const { data: products, isLoading } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await api.get("/products")).data,
    });

    const filtered = useMemo(() => {
        let list = products || [];
        if (filter === "Bestsellers") list = list.filter((p) => ["Bestseller", "Fan Favorite"].includes(p.badge));
        else if (filter === "New") list = list.filter((p) => p.badge === "New");
        else if (filter === "Seasonal") list = list.filter((p) => p.badge === "Seasonal");
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter((p) => p.name.toLowerCase().includes(s) || p.flavor.toLowerCase().includes(s));
        }
        return list;
    }, [products, filter, q]);

    return (
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
            <Reveal>
                <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Shop All</p>
                <h1 className="mt-2 font-serif text-4xl font-semibold text-roast sm:text-5xl">Every flavor, freshly made</h1>
                <p className="mt-3 max-w-lg text-mocha">Browse our full menu of handcrafted banana pudding. Order single cups, large cups or party trays.</p>
            </Reveal>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setFilter(f)}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === f ? "bg-roast text-cream-50" : "bg-cream-200 text-roast hover:bg-cream-300"}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
                <div className="relative sm:w-72">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
                    <Input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search flavors…" className="h-11 rounded-full border-caramel/15 bg-white pl-11 focus-visible:ring-caramel" />
                </div>
            </div>

            <Stagger className="mt-20 grid grid-cols-1 gap-x-6 gap-y-20 sm:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                ) : filtered.length ? (
                    filtered.map((p) => <ProductCard key={p.id} product={p} />)
                ) : (
                    <p className="col-span-full py-16 text-center text-mocha">No flavors match your search.</p>
                )}
            </Stagger>
        </div>
    );
}
